<template>
  <b-row class="mx-1 py-2">
    <b-col cols="12" md="4" class="p-0">
      <b-card header="Customer" header-bg-variant="nav">
        <div class="d-flex">
          <multiselect
            v-model="customer"
            :options="customers"
            :close-on-select="true"
            :clear-on-select="false"
            placeholder="Standard Customer"
            track-by="companyName"
            label="companyName"
            select-label=""
            :searchable="true"
            :custom-label="customLabel"
            :allow-empty="!_isCustomerSite(site.Name)">
          </multiselect>
          <b-button-group v-if="customer !== null">
            <b-button variant="outline-danger" size="sm" title="Unselect Customer" @click="() => customer = null" v-if="!_isCustomerSite(site.Name)">
              <b-icon icon="x" aria-hidden="true"></b-icon>
            </b-button>
            <b-button class="d-flex align-items-center" variant="outline-primary" size="sm" title="Customer Details" target="_blank" :href="'/customer/' + customer.customerNo">
              <b-icon icon="person-fill" aria-hidden="true"></b-icon>
            </b-button>
          </b-button-group>
        </div>
      </b-card>
      <b-card header="Catalog" header-bg-variant="nav">
        <div class="d-flex">
          <multiselect
            v-model="catalogSelection"
            :options="allCatalogs"
            :close-on-select="true"
            :clear-on-select="false"
            placeholder="All catalogs"
            track-by="CatalogId"
            label="DisplayName"
            select-label=""
            :searchable="true"
          >
          </multiselect>
        </div>
      </b-card>
      <b-card v-if="site.ShowInventory || site.HasPricing" header="Export Fields" header-bg-variant="nav">
        <div class="row">
          <div class="col-md-6">
            <b-input-group>
              <b-checkbox switch inline v-model="showInventory" v-if="site.ShowInventory">Inventory</b-checkbox>
              <b-checkbox switch inline v-model="showPrice" v-if="site.HasPricing">Pricing</b-checkbox>
            </b-input-group>
          </div>
          <div class="col-md-6 text-right">
            <b-button variant="outline-primary" size="sm" v-if="customer && customer.hasCustomerMaterialNumbers" @click="exportCustomerMaterial">
              Export All Customer Material Numbers
            </b-button>
            <b-button variant="outline-primary" size="sm" @click="exportAll">
              Exports ALL Categories
            </b-button>
          </div>
        </div>
      </b-card>
      <!-- TODO: recent -->
      <b-card no-body>
        <b-tabs pills card size="sm">
          <b-tab no-body title="Browse" active>
            <tree treeTitle="Topology"
              :entityObject="entityObjectLookup.category"
              :getItems="getTopologyTree"
              :childProperties="childProperties"
              @itemSelected="(toplogyItem, entityObject) => topologyClick(toplogyItem, entityObject)"
              :activeItem="activeItem"
              :showHeader="false"
            />
          </b-tab>
          <b-tab title="Series">
            <b-input-group label-cols="2" size="sm">
              <b-form-input autofocus v-model="seriesNumber" size="sm" placeholder="Series" @keyup.enter.native="searchBySeries()" />
              <b-button title="Search" variant="light" size="sm" @click.stop="searchBySeries()">
                <b-icon icon="search" variant="success" aria-hidden="true"></b-icon>
              </b-button>
            </b-input-group>
          </b-tab>
          <b-tab title="Catalog Number">
            <div class="row">
              <div class="col-sm col-md-10">
                <b-form-input type="text" class="form-control search-input" placeholder="Enter a Catalog Number" :value="catalogNumber" @input="asyncFind" :v-model="catalogNumber" />
                <loading-text v-if="searchResults.loading" />
                <b-list-group class="result-list" v-if="searchResults.Count > 0">
                  <!-- @click="clearResults" -->
                  <b-list-group-item v-for="result in searchResults.List" :key="result.PcnCatalogNumber" class="text-left click" @click="selectModelResult(result)">
                    <p class="mb-1 font-weight-bold">
                      {{ customer?.customerNo ? (result.CustomerMaterialNumber ?? result.PcnCatalogNumber) : result.PcnCatalogNumber }} - {{ result.FingoodTpiMfgMod }}
                    </p>
                    <span class="small">{{ result.SapModelDescription }}</span>
                  </b-list-group-item>
                </b-list-group>
              </div>
              <div class="col-sm col-md-2">
                <b-button title="Search" variant="light" @click.stop="searchByCatalogNumber()">
                  <b-icon icon="search" variant="success" aria-hidden="true"></b-icon>
                </b-button>
              </div>
            </div>
          </b-tab>
          <b-tab title="Material Group" v-if="!_isCustomerSite(site.Name)">
            <div class="row">
              <div class="col-sm col-md-12">
                <template v-if="!visibleMaterialGroupResults.loading">
                  <multiselect
                    v-model="materialGroup"
                    deselect-label="Selected"
                    :allow-empty="false"
                    :options="searchMaterialGroupResults.List"
                    :close-on-select="true"
                    :clear-on-select="false"
                    placeholder="Select a Material Group"
                    track-by="MaterialGroupNumber"
                    :custom-label="customMaterialLabel"
                    :searchable="true"
                    @input="selectMaterialGroupResult">
                  </multiselect>
                </template>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </b-card>
    </b-col>
    <b-col cols="8" class="p-0">
      <model-viewer :catalogNumber="catalogNumberInternal" :seriesNumber="seriesNumberInternal" :productId="productId" :manufacturingSeriesId="manufacturingSeriesId" :productClassId="productClassId" :categoryId="categoryId" :showInventory="showInventory" :showPrice="showPrice" :selectedItemDisplayName="selectedItemDisplayName" :materialGroup="materialGroupInternal" :customerNumber="selectedCustomerNumber" :topologyItem="topologyItem" :customer="customer">
        <template #sectionTableHeader="sectionTable">
          <b-button title="Export Excel" @click="getExportSectionTable(sectionTable.SectionTableId)" target="_blank" variant="link" v-if="catalogNumberInternal === ''">
            <b-icon icon="table" />
          </b-button>
        </template>
        <template #sectionTabContentHeader="sectionTab">
          <b-button title="Export Excel" @click="getExportSectionTab(sectionTab.SectionTabId)" variant="link" target="_blank">
            <h6> <b-icon icon="table" /> Export Tab {{ sectionTab.DisplayName }} </h6>
          </b-button>
        </template>
        <template #rowHeader="model">
          <b-button title="Export Excel" @click="getExportModel(model.PcnCatalogNumber)" variant="link" target="_blank">
            <b-icon icon="table" />
          </b-button>
        </template>
      </model-viewer>
    </b-col>
  </b-row>
</template>
<script lang="ts">
import { Component, Watch } from 'vue-property-decorator'
import ModelViewer from '@/components/shared/model-viewer/viewer.vue'
import Tree from '@/components/shared/tree/tree.vue'
import MxSite from '@/mixins/site'
import { namespace } from 'vuex-class'
import { EntityObject } from '../../store/entity/types'
import Multiselect from 'vue-multiselect'
import SearchInput from '../search/search-input.vue'
import { debounce } from 'typescript-debounce-decorator'
import { ResponseArray } from '@/store/types'
import { Model } from '@/store/editor-model/types'
import { blankResponseArray } from '@/store/constants'
import type { MaterialGroup, VisibleModelSearchRequestWithCustomerNumber } from '@/store/search/types'
import { blankVisibleModelSearchRequest } from '@/store/search/constants'
import LoadingText from '@/components/utility/loading-text.vue'
import ExportSelector from '@/components/shared/model-viewer/export-selector/export-selector.vue'
import { blankMaterialGroup } from '@/store/model-viewer/constants'

// TODO: don't get from model-viewer- get from somewhere else!
const nsModelViewer = namespace('model-viewer')
const nsSearch = namespace('search')

@Component({
  components: {
    'model-viewer': ModelViewer,
    'tree': Tree,
    Multiselect,
    SearchInput,
    'loading-text': LoadingText,
    'export-selector': ExportSelector,
  },
})
export default class extends MxSite {
  public customers = []
  public customer: any = null

  public catalogNumber = ''
  public catalogNumberInternal = ''
  public selectedCustomerNumber = ''

  public materialGroup: any = null
  public materialGroupInternal: MaterialGroup = blankMaterialGroup

  public seriesNumber = ''
  public seriesNumberInternal = ''

  public productId = -1
  public manufacturingSeriesId = -1
  public productClassId = -1
  public categoryId = -1
  public selectedItemDisplayName = ''

  // TODO: make a TopologyItem
  public activeItem: any = {}
  public showInventory = true
  public showPrice = true
  public topologyItem: any = null
  public allCategoryTopologyItem: any = null
  public showExportSelector = false
  public loading = false
  public allCatalogs = []
  public catalogSelection: any = null

  // TODO: move this function or copy it or what?
  @nsModelViewer.Action('getTopology')
  public getTopology: any

  @nsModelViewer.Action('getCatalogs')
  public getCatalogs: any

  @nsModelViewer.Action('setCatalogId')
  public setCatalogId: any

  @nsSearch.Action('searchVisibleModelsWithCustomerNumber')
  public searchVisibleModelsWithCustomerNumber: any

  @nsSearch.Action('getMaterialGroups')
  public getMaterialGroups: any

  @nsSearch.State('visibleModelsResults')
  public visibleModelsResults!: ResponseArray<Model>

  @nsSearch.State('visibleModelResultsByMaterialGroup')
  public visibleModelResultsByMaterialGroup!: ResponseArray<Model>

  @nsSearch.State('visibleMaterialGroupResults')
  public visibleMaterialGroupResults!: ResponseArray<MaterialGroup>

  @nsSearch.Mutation('clearResults')
  public clearResults: any

  @nsModelViewer.Action('getExportFile')
  public getExportFile: any

  async mounted() {
    this.showInventory = this.site.ShowInventory
    this.showPrice = this.site.HasPricing
    await this.$store.dispatch('multiplier/clearCustomerMultipliers')
    await this.fetchCustomers()
    this.getMaterialGroups()
    this.loading = true
    this.getCatalogs().then((items) => {
      this.allCatalogs = items
    })
    this.getTopology().then((items) => {
      this.topologyItem = items
      this.allCategoryTopologyItem = items
      this.loading = false
    })
  }

  get childProperties() {
    const { entityObjectLookup } = this
    return [
      entityObjectLookup.category.Properties.find(f => f.EntityPropertyName === 'ProductClasses'),
      entityObjectLookup.productClass.Properties.find(f => f.EntityPropertyName === 'ManufacturingSeries'),
      entityObjectLookup.manufacturingSeries.Properties.find(f => f.EntityPropertyName === 'Products'),
    ]
  }

  async fetchCustomers() {
    try {
      await this.$store.dispatch('order-state/fetchCustomers', { keyword: '', includeCustomerMaterialInfo: true })
      this.customers = this.$store.getters['order-state/customers']
      if (this._isCustomerSite(this.site.Name) && this.customers.length > 0) {
        this.customer = this.customers[0]
      }
    } catch (error) {
      console.error('Error fetching customers:', error)
    }
  }

  public clearAll() {
    this.catalogNumberInternal = ''
    this.seriesNumberInternal = ''
    this.categoryId = -1
    this.productClassId = -1
    this.manufacturingSeriesId = -1
    this.productId = -1
    this.selectedItemDisplayName = ''
    this.materialGroup = ''
    this.materialGroupInternal = blankMaterialGroup
  }

  public searchByCatalogNumber() {
    if (this.catalogNumber.trim() === '') return
    this.clearAll()
    this.catalogNumberInternal = this.catalogNumber
  }

  public searchBySeries() {
    this.clearAll()
    this.catalogNumberInternal = ''
    this.seriesNumberInternal = this.seriesNumber
  }

  public topologyClick(topologyItem: any, entityObject: EntityObject) {
    this.clearAll()
    this.topologyItem = topologyItem
    switch (entityObject.EntityName) {
      case 'Product':
        this.productId = topologyItem.ProductId
        break
      case 'ManufacturingSeries':
        this.manufacturingSeriesId = topologyItem.ManufacturingSeriesId
        break
      case 'ProductClass':
        this.productClassId = topologyItem.ProductClassId
        this.selectedItemDisplayName = topologyItem.DisplayName
        break
      case 'Category':
        this.categoryId = topologyItem.CategoryId
        this.selectedItemDisplayName = topologyItem.DisplayName
        break
    }
  }

  @Watch('customer')
  public async loadCustomerMultipliers() {
    if (this.customer) {
      this.selectedCustomerNumber = this.customer.customerNo
      await this.$store.dispatch('multiplier/fetchCustomerMultipliers', this.customer.customerNo)
      this.asyncFind(this.catalogNumber)
    } else {
      this.selectedCustomerNumber = ''
      await this.$store.dispatch('multiplier/clearCustomerMultipliers')
    }
  }

  @Watch('catalogSelection')
  public async catalogIdChanged() {
    this.setCatalogId({ catalogId: this.catalogSelection?.CatalogId ?? null })
    this.setReloadTree({ value: true })
  }

  public async getTopologyTree() {
    return this.getTopology().then((items) => {
      this.allCategoryTopologyItem = items
      return items
    })
  }

  getExportSectionTable(sectionTableId: any) {
    this.getExportFile({ sectionTableId, customerNumber: this.customer?.customerNo, showInventory: this.showInventory, showPricing: this.showPrice })
  }

  getExportSectionTab(sectionTabId: any) {
    this.getExportFile({ sectionTabId, customerNumber: this.customer?.customerNo, showInventory: this.showInventory, showPricing: this.showPrice })
  }

  getExportModel(pcnCatalogNumber: any) {
    this.getExportFile({ pcnCatalogNumber, customerNumber: this.customer?.customerNo, showInventory: this.showInventory, showPricing: this.showPrice })
  }

  public exportCustomerMaterial() {
    this.getExportFile({ customerNumber: this.customer?.customerNo, showInventory: this.showInventory, showPricing: this.showPrice })
  }

  public exportAll() {
    this.getExportFile({ all: true, customerNumber: this.customer?.customerNo, showInventory: this.showInventory, showPricing: this.showPrice })
  }

  public customLabel({
    companyName,
    customerNo,
  }: {
    companyName: string
    customerNo: string
  }) {
    return `${companyName} - ${customerNo}`
  }

  public customMaterialLabel({
    MaterialGroupNumber,
    MaterialGroupDescription,
  }: {
    MaterialGroupNumber: string
    MaterialGroupDescription: string
  }) {
    return `${MaterialGroupNumber} - ${MaterialGroupDescription}`
  }

  public searchResults: ResponseArray<Model> = { ...blankResponseArray, loading: false }
  public searchResultsByMaterialGroup: ResponseArray<Model> = { ...blankResponseArray, loading: false }
  public searchMaterialGroupResults: ResponseArray<MaterialGroup> = { ...blankResponseArray, loading: false }

  public selectModelResult(model: Model) {
    this.catalogNumber = model.PcnCatalogNumber
    this.searchByCatalogNumber()
    this.clearResults()
  }

  public selectMaterialGroupResult(materialGroup: MaterialGroup) {
    this.clearAll()
    this.materialGroupInternal = materialGroup
  }

  @debounce(1000)
  public async asyncFind(query: string) {
    this.catalogNumber = query
    this.handleSearch(query)
  }

  public handleSearch(searchTerm: string) {
    const search: VisibleModelSearchRequestWithCustomerNumber = {
      ...blankVisibleModelSearchRequest,
      Keyword: searchTerm,
      CustomerNumber: this.customer?.customerNo === undefined ? '' : this.customer?.customerNo,
    }
    this.searchVisibleModelsWithCustomerNumber(search)
  }

  public openExportSelector(show: boolean) {
    this.loading = show
    this.showExportSelector = show
  }

  @Watch('visibleModelsResults', { deep: true })
  public visibleModelsResultsUpdated(results: ResponseArray<Model>) {
    const resultItems: Model[] = []

    results.List.forEach(async (model) => {
      resultItems.push(model)
    })
    this.searchResults = { ...results, List: resultItems }
  }

  @Watch('visibleModelResultsByMaterialGroup', { deep: true })
  public visibleModelsResultsByMaterialGroupUpdated(results: ResponseArray<Model>) {
    const resultItems: Model[] = []
    results.List.forEach(async (model) => {
      resultItems.push(model)
    })
    this.searchResultsByMaterialGroup = { ...results, List: resultItems }
  }

  @Watch('visibleMaterialGroupResults', { deep: true })
  public visibleMaterialGroupResultsUpdated(results: ResponseArray<MaterialGroup>) {
    const resultItems: MaterialGroup[] = []

    results.List.forEach(async (materialGroup) => {
      resultItems.push(materialGroup)
    })
    this.searchMaterialGroupResults = { ...results, List: resultItems }
  }
}

</script>
<style scoped>
.click {
  cursor: pointer;
}

.click:hover {
  background-color: #cacace;
}
</style>
